import { Timestamp } from '@angular/fire/firestore';

/**
 * Counting status for each item in a stock take.
 * PENDING  – added to the stock take but not yet scanned/counted.
 * COUNTED  – item was scanned and is part of the original stock take.
 * UNIQUE   – item was scanned but was NOT part of the original stock take.
 */
export enum StockTakeItemStatus {
  pending = 'PENDING',
  counted = 'COUNTED',
  unique = 'UNIQUE',
  recount = 'RECOUNT',
}

/**
 * Barcode link staged during counting; applied to stock when the stock take is processed.
 */
export interface IStockTakePendingBarcodeUpdate {
  mode: 'primary' | 'multiple';
  scannedBarcode: string;
}

/**
 * Stock take item line (stored in item_chunks, keyed by encoded stock code).
 */
export interface IStockTakeItem {
  code: string;
  id: string;
  previousQtyCounted: number;
  qtyCounted: number;
  status: StockTakeItemStatus;
  timesCounted: number;
  /** Timestamp when the item was first added to the stock take */
  addedAt?: Timestamp;
  /** Bin location assigned to this item at the time of counting */
  binLocation?: string;
  /** Email address of the user who last counted this item */
  countedByEmail?: string;
  /** Timestamp when the item was counted for the first time — not updated on subsequent counts */
  firstCountedAt?: Timestamp;
  /** Barcode assignment to apply when this stock take is processed */
  pendingBarcodeUpdate?: IStockTakePendingBarcodeUpdate;
}

/**
 * Stock item data returned from the Gallix cloud API for a given code.
 */
export interface IStockItemApiData {
  delColMovement: number;
  description: string;
  latestCost: number;
  movement: number;
  onhand: number;

  avCost?: number;
  barcode?: string;
  binL?: string;
  category?: string;
  created?: string | number;
  dep?: string;
  genCode?: string;
  gp1?: number;
  lastSold?: string | number;
  /** ISO date string from stock `_stock_take_date` (or TypeSense). */
  lastStockTake?: string;
  lineColourCode?: number;
  /** Raw stock field 45 (noDecimal). Use resolveStockTakeNoDecimal() before UI rules. */
  noDecimal?: string | number | boolean;
  nominalGP?: number;
  onHoldCode?: number;
  ordLvl?: number;
  orderQuantity?: number;
  packSize?: number;
  purchaseOrder?: number;
  regularSuppCode?: string;
  /** Stock field 37 — parent item this line reports to when counting. */
  reportItemCode?: string;
  /** Stock field 38 — multiplier applied when counting a reporting item. */
  reportItemFactor?: number;
  sellPriExcl1?: number;
  sellPriIncl1?: number;
  subDep?: string;
  suppCode?: string;
  unitsYear?: number;
  vatR?: number;
}

/**
 * Enriched stock take item combining Firestore counts with API-provided product data.
 * variance and randVariance are computed on the frontend.
 */
export interface IStockTakeItemDetail {
  /** Firestore document ID of the underlying IStockTakeItem */
  code: string;
  delColMovement: number;
  delColQty: number;
  description: string;
  id: string;
  latestCost: number;
  movement: number;
  onhand: number;
  previousQtyCounted: number;
  qtyCounted: number;
  /** Del-col quantity from the stock file (raw field — not a movement calculation) */
  randVariance: number;
  status: StockTakeItemStatus;
  timesCounted: number;
  variance: number;

  avCost?: number;
  barcode?: string;
  binL?: string;
  binLocation?: string;
  category?: string;
  countedByEmail?: string;
  created?: string | number;
  dep?: string;
  genCode?: string;
  gp1?: number;
  lastSold?: string | number;
  lastStockTake?: string;
  lineColourCode?: number;
  /** Raw stock field 45 (noDecimal). Use resolveStockTakeNoDecimal() before UI rules. */
  noDecimal?: string | number | boolean;
  nominalGP?: number;
  onHoldCode?: number;
  ordLvl?: number;
  orderQuantity?: number;
  packSize?: number;
  /** Barcode will be updated on stock when this stock take is processed */
  pendingBarcodeUpdate?: IStockTakePendingBarcodeUpdate;
  purchaseOrder?: number;
  regularSuppCode?: string;
  reportItemCode?: string;
  reportItemFactor?: number;
  sellPriExcl1?: number;
  sellPriIncl1?: number;
  subDep?: string;
  suppCode?: string;
  unitsYear?: number;
  vatR?: number;
}

/**
 * Stock movement data for a single item, mapped from the Gallix API response.
 */
export interface IStockItemMovement {
  code: string;
  /** Del-col movement since the stock take start time */
  delColMovement: number;
  /** Net quantity in/out since the stock take start time */
  stockMovement: number;
}

/**
 * Final figure stored after a stock take is submitted.
 * Path: .../finalised_chunks/{chunkId} entries keyed by encoded stock code.
 */
export interface IStockTakeFinalFigure {
  code: string;
  id: string;
  /** Legacy variance quantity kept for backend compatibility */
  varianceQty: number;
  /** Average cost at finalisation — used to compute randVariance */
  avCost?: number;
  /** Bin location assigned when the item was counted */
  binLocation?: string;
  /** Email of the user who last counted this item before finalisation */
  countedByEmail?: string;
  delColMovement?: number;
  /** Del-col quantity from the stock file (field 35) at finalisation */
  delColQty?: number;
  description?: string;
  latestCost?: number;
  movement?: number;
  onhand?: number;
  qtyCounted?: number;
  /** Value variance (variance × avCost) — legacy field name kept for backend compatibility */
  randVariance?: number;
  timesCounted?: number;
  variance?: number;
}
